import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { fmtDate } from '../../lib/format';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function AdminAvailabilityPage() {
  const [advisors, setAdvisors] = useState([]);
  const [advisorId, setAdvisorId] = useState('');
  const [rules, setRules] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [newBlock, setNewBlock] = useState({ start: '', end: '', reason: '' });

  useEffect(() => { api.adminAdvisors().then((a) => { setAdvisors(a); if (a[0]) setAdvisorId(a[0].id); }); }, []);

  const load = (id) => {
    if (!id) return;
    api.getAvailability(id).then(setRules);
    api.listBlocked(id).then(setBlocked);
  };
  useEffect(() => { load(advisorId); }, [advisorId]);

  const ruleFor = (wd) => rules.find((r) => r.weekday === wd);

  const setRule = (wd, patch) => {
    const ex = ruleFor(wd);
    if (ex) setRules(rules.map((r) => r.weekday === wd ? { ...r, ...patch } : r));
    else setRules([...rules, { advisor_id: advisorId, weekday: wd, start_time: '09:00', end_time: '17:00', active: true, ...patch }]);
  };

  const toggleDay = (wd) => {
    const ex = ruleFor(wd);
    if (ex) setRules(rules.filter((r) => r.weekday !== wd));
    else setRule(wd, {});
  };

  const save = async () => {
    try {
      await api.setAvailability(advisorId, rules.map(({ advisor_id, weekday, start_time, end_time, active = true }) => ({ advisor_id: advisorId, weekday, start_time, end_time, active })));
      toast.success('Availability saved'); load(advisorId);
    } catch { toast.error('Save failed'); }
  };

  const addBlock = async () => {
    if (!newBlock.start || !newBlock.end) return toast.error('Pick start and end');
    try {
      await api.createBlocked({
        advisor_id: advisorId,
        start: new Date(newBlock.start).toISOString(),
        end: new Date(newBlock.end).toISOString(),
        reason: newBlock.reason,
      });
      setNewBlock({ start: '', end: '', reason: '' });
      toast.success('Block added'); load(advisorId);
    } catch { toast.error('Failed'); }
  };

  const removeBlock = async (id) => {
    await api.deleteBlocked(id); load(advisorId);
  };

  return (
    <AdminLayout title="Availability" subtitle="Weekly hours and blocked times per advisor"
      actions={
        <select value={advisorId} onChange={(e) => setAdvisorId(e.target.value)} className="px-3 py-2 rounded-md border border-neutral-200 text-[13px] focus:border-[#f5b300] outline-none">
          {advisors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl bg-white border border-neutral-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[17px] font-bold text-[#14110f]">Weekly hours</h3>
            <button onClick={save} className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] px-4 py-2 rounded-md text-[13px] font-semibold"><Save className="w-4 h-4" /> Save</button>
          </div>
          <div className="space-y-3">
            {DAYS.map((day, wd) => {
              const r = ruleFor(wd);
              const on = !!r;
              return (
                <div key={day} className="flex flex-wrap items-center gap-3 py-2 border-b border-neutral-50 last:border-0">
                  <label className="flex items-center gap-2 w-32">
                    <input type="checkbox" checked={on} onChange={() => toggleDay(wd)} className="accent-[#f5b300]" />
                    <span className="text-[14px] font-medium">{day}</span>
                  </label>
                  {on ? (
                    <>
                      <input type="time" value={r.start_time} onChange={(e) => setRule(wd, { start_time: e.target.value })} className="px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
                      <span className="text-neutral-400">to</span>
                      <input type="time" value={r.end_time} onChange={(e) => setRule(wd, { end_time: e.target.value })} className="px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
                    </>
                  ) : (
                    <span className="text-[12.5px] text-neutral-400">Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-neutral-100 p-6">
          <h3 className="text-[17px] font-bold text-[#14110f] mb-4">Blocked time</h3>
          <div className="space-y-2 mb-4">
            <input type="datetime-local" value={newBlock.start} onChange={(e) => setNewBlock({ ...newBlock, start: e.target.value })} className="w-full px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
            <input type="datetime-local" value={newBlock.end} onChange={(e) => setNewBlock({ ...newBlock, end: e.target.value })} className="w-full px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
            <input value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} placeholder="Reason (optional)" className="w-full px-3 py-2 rounded-md border border-neutral-200 text-[13px]" />
            <button onClick={addBlock} className="w-full inline-flex items-center justify-center gap-2 bg-[#14110f] hover:bg-[#2a2622] text-white px-4 py-2 rounded-md text-[13px] font-semibold"><Plus className="w-4 h-4" /> Add block</button>
          </div>
          <ul className="divide-y divide-neutral-100">
            {blocked.length === 0 && <p className="text-[12.5px] text-neutral-400">No blocks.</p>}
            {blocked.map((b) => (
              <li key={b.id} className="py-2.5 flex items-center justify-between">
                <div className="text-[12.5px]">
                  <p className="font-medium text-[#14110f]">{fmtDate(b.start)} – {fmtDate(b.end)}</p>
                  {b.reason && <p className="text-neutral-500">{b.reason}</p>}
                </div>
                <button onClick={() => removeBlock(b.id)} className="w-8 h-8 rounded-md hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
